import { Licensing_V1 } from "@rw1/auto-generated-clients";
import { CellContext, ColumnDef, SortingState } from "@tanstack/react-table";
import { FC, useCallback, useMemo, useState } from "react";
import { DataTable } from "../../../../components/data-table/DataTable";
import { DeleteActionCell } from "../../../../components/data-table/DeleteActionCell";
import { useLocalization } from "../../../../hooks/useLocalization";
import { formatDateYMD } from "../../../../shared/utils/date";
import { LicenseWithExperienceName } from "../../hooks/useLicenseById";
import { AddToWhitelistDialog } from "../AddToWhitelistDialog";
import { RemoveWhitelistedEmail } from "../RemoveWhitelistedEmail";

interface WhitelistTabProps {
  license: LicenseWithExperienceName | undefined;
  whitelist?: Licensing_V1.WhitelistEntry[];
}

const INITIAL_SORTING: SortingState = [{ id: "email", desc: false }];

export const WhitelistTab: FC<WhitelistTabProps> = ({
  license,
  whitelist = [],
}) => {
  const { t } = useLocalization();

  const [isAddToWhitelistDialogOpen, setIsAddToWhitelistDialogOpen] =
    useState(false);
  const [
    isRemoveWhitelistedEmailDialogOpen,
    setIsRemoveWhitelistedEmailDialogOpen,
  ] = useState(false);
  const [removedEmail, setRemovedEmail] = useState<string>("");

  const handleRemoveFromGroup = (entry: Licensing_V1.WhitelistEntry) => {
    setIsRemoveWhitelistedEmailDialogOpen(true);
    setRemovedEmail(entry.email);
  };

  const handleCancelRemove = () => {
    setIsRemoveWhitelistedEmailDialogOpen(false);
    setRemovedEmail("");
  };

  const renderActionCell = useCallback(
    (ctx: CellContext<Licensing_V1.WhitelistEntry, any>) => {
      const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        handleRemoveFromGroup(ctx.row.original);
      };

      return (
        <DeleteActionCell
          actionText={t("licenses.whitelistTab.removeEmail")}
          onClick={handleClick}
        />
      );
    },
    [t],
  );

  const columns = useMemo<ColumnDef<Licensing_V1.WhitelistEntry, any>[]>(
    () => [
      {
        id: "email",
        header: t("licenses.whitelistTab.emailHeader"),
        accessorKey: "email",
        enableSorting: true,
        enableColumnFilter: false,
        sortingFn: "text",
        size: 350,
      },
      {
        id: "added",
        accessorKey: "createdAt",
        accessorFn: (row) => formatDateYMD(row.createdAt),
        header: t("licenses.whitelistTab.addedHeader"),
        enableSorting: true,
        enableColumnFilter: false,
        size: 150,
      },
      {
        id: "actions",
        header: undefined,
        cell: renderActionCell,
        size: 70,
        enableSorting: false,
        enableColumnFilter: false,
      },
    ],
    [t, renderActionCell],
  );

  return (
    <>
      <DataTable<Licensing_V1.WhitelistEntry>
        actionButtonText={t("licenses.whitelistTab.addUserToWhitelist")}
        actionButtonTextShort={t("licenses.whitelistTab.add")}
        columns={columns}
        data={whitelist}
        defaultSort={INITIAL_SORTING}
        globalFilterPlaceholder={t("licenses.whitelistTab.searchUsers")}
        onActionClick={() => setIsAddToWhitelistDialogOpen(true)}
        showActionButton={true}
      />
      <AddToWhitelistDialog
        isOpen={isAddToWhitelistDialogOpen}
        licenseId={license?.licenseId ?? ""}
        onClose={() => setIsAddToWhitelistDialogOpen(false)}
        whiteListedEmails={whitelist.map((entry) => entry.email)}
      />
      <RemoveWhitelistedEmail
        licenseId={license?.licenseId ?? ""}
        licenseName={license?.experienceName ?? ""}
        onClose={handleCancelRemove}
        open={isRemoveWhitelistedEmailDialogOpen}
        removedEmail={removedEmail}
      />
    </>
  );
};
